import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { IconArrowRight, IconCalculator, IconLayoutColumns, IconHomeDollar } from "@tabler/icons-react";
import classNames from "classnames";
import { NavbarTheme, NAVBAR_THEMES } from "../../constants/themes";
import { NavSectionConfig, MegaMenuContent } from "../../types/navbar";


const FALLBACK_ICONS = {
  calculator: <IconCalculator size={48} className="text-primary" />,
  comparator: <IconLayoutColumns size={48} className="text-primary" />,
  capacity:   <IconHomeDollar size={48} className="text-primary" />,
};

const ToolIcon = ({ src, alt, fallbackIcon }: { src: string; alt: string; fallbackIcon?: string }) => {
  const [error, setError] = useState(false);
  if (error) return <>{FALLBACK_ICONS[fallbackIcon as keyof typeof FALLBACK_ICONS] ?? <IconCalculator size={48} className="text-primary" />}</>;
  return <img src={src} alt={alt} className="w-12 h-12 shrink-0" onError={() => setError(true)} />;
};

type Props = {
  section: NavSectionConfig;
  content: MegaMenuContent | null;
  mode: NavbarTheme;
  slideDir: number;
  isSwitching: boolean;
  onClose: () => void;
};

const MegaMenu = ({ section, content, mode, slideDir, isSwitching, onClose }: Props) => {
  const theme = NAVBAR_THEMES[mode];

  return (
    <div
      onMouseLeave={onClose}
      className={classNames(
        "w-full rounded-b-[40px] px-8 py-8 border-b border-x",
        theme.megaMenuBg,
        theme.megaMenuBorder,
      )}
    >
      <div className="mx-auto max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-7xl overflow-hidden">
        <AnimatePresence mode="wait" custom={slideDir}>
        <motion.div
          key={section.title}
          custom={slideDir}
          initial={{ x: isSwitching ? slideDir * 40 : 0, opacity: isSwitching ? 0 : 1 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: isSwitching ? -slideDir * 40 : 0, opacity: isSwitching ? 0 : 1 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
        {content ? (
          <>
            <div className="flex gap-0">
              {content.columns.map((col) => (
                <div
                  key={col.title}
                  className={classNames(
                    "pr-8 mr-8 border-r",
                    theme.megaMenuBorder,
                  )}
                >
                  <h4 className={classNames("font-semibold text-base mb-4", theme.text)}>
                    {col.title}
                  </h4>
                  <ul className={classNames("flex gap-x-8 gap-y-3", col.twoCol ? "grid grid-cols-2" : "flex-col")}>
                    {col.links.map((item) => (
                      <li key={item.link}>
                        <Link
                          to={item.link}
                          onClick={onClose}
                          className={classNames("text-sm transition-colors", item.green ? "" : theme.megaMenuLink)}
                          style={item.green ? { color: "#0AD581" } : undefined}
                        >
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {content.tools && (
                <div className="pr-8 mr-8">
                  <h4 className={classNames("font-semibold text-base mb-4", theme.text)}>Herramientas</h4>
                  <ul className="flex flex-col gap-y-1">
                    {content.tools.map((tool) => (
                      <li key={tool.link} className="p-0 m-0">
                        <Link to={tool.link} onClick={onClose} className="flex items-center gap-3 group py-1">
                          <ToolIcon src={tool.icon} alt={tool.name} fallbackIcon={tool.fallbackIcon} />
                          <span className="text-primary text-sm font-medium group-hover:underline leading-tight max-w-[140px]">
                            {tool.name}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {content.footerLink && (
              <div className="mt-6  pt-4">
                <Link to={content.footerLink.link} onClick={onClose} className="flex items-center gap-1 text-primary font-base font-semibold !font-grotesk hover:underline">
                  {content.footerLink.name}
                  <IconArrowRight size={16} />
                </Link>
              </div>
            )}
          </>
        ) : (
          <ul className="flex flex-col gap-3">
            {section.links.map((item) => (
              <li key={item.link}>
                <Link to={item.link} onClick={onClose} className={classNames("text-sm transition-colors", theme.megaMenuLink)}>
                  {item.name}
                </Link>
              </li>
            ))}
          </ul>
        )}
        </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MegaMenu;
